// --- Logic --------------------------------------------------------------------------------------
export type TyPreviewKind = "image" | "video" | "pdf" | "text";

/**
 * What the browser can render on its own, by extension.
 *
 * These lists are **narrower than the categories a file can be uploaded as**, and deliberately so.
 * `FILE_CATEGORIES` says what we accept; this says what can be shown without shipping a library, and
 * the gap is the honest answer rather than a broken viewer:
 *
 * - **HEIC** is missing because Chrome and Firefox cannot decode it at all. An iPhone photo has no
 *   preview and no thumbnail today, and the fix belongs at upload, not here.
 * - **TIFF** has no browser decoder either.
 * - **MKV and AVI** are containers no browser plays.
 * - **Office files and CAD** need a library or a paid service — see the plan.
 */
const kinds: Record<TyPreviewKind, string[]> = {
    image: ["jpg", "jpeg", "png", "webp", "gif", "avif", "svg", "bmp"],
    video: ["mp4", "webm", "mov", "m4v"],
    pdf: ["pdf"],
    text: ["txt", "csv", "md", "json"],
};

/** `null` means "download it" — which is a better answer than an empty frame. */
export function resolvePreviewKind(fileName?: string | null): TyPreviewKind | null {
    const parts = String(fileName ?? "").split(".");
    if (parts.length < 2) return null;

    const extension = (parts.pop() as string).toLowerCase().replace(/[^a-z0-9]/g, "");
    for (const [kind, extensions] of Object.entries(kinds)) {
        if (extensions.includes(extension)) return kind as TyPreviewKind;
    }
    return null;
}
